// src/lib/crypto.js
// PATCH (this version):
//   FIX #12 (Issue 12): Export clearCryptoSalt() so AuthContext can call it
//     during sign-out. Previously the crypto salt stored in localStorage
//     ('ds_crypto_salt') was never cleared on sign-out. On a shared device,
//     User B would inherit User A's salt, meaning User B's encrypted data was
//     derived from User A's salt — a cross-user encryption isolation breach.
//     clearCryptoSalt() removes the salt. The next call to getOrCreateSalt()
//     (on User B's first write) generates a fresh random salt for User B.
//
//   NOTE: After this fix, a user who has a PIN set and then signs out will
//     need to re-encrypt their data when they next sign in, because their new
//     salt won't match what was used to encrypt their old data. In practice
//     this only matters if you support per-user encryption on a shared device.
//     If all users on a device use the same account this has no effect.
//
// All other crypto logic is unchanged.
// ─────────────────────────────────────────────────────────────────────────────

import { auth } from '@/lib/firebase';

const ALGO       = 'AES-GCM';
const KEY_LENGTH = 256;
const ITERATIONS = 310000; // PBKDF2 iterations — OWASP 2023 recommendation
const SALT_KEY   = 'ds_crypto_salt'; // fallback key used only when no uid is available yet

// FIX #6 (Flag 6): Salt is now keyed per-uid so it persists across sign-out/
// sign-in for the SAME user, instead of being tied to the device/session.
// Falls back to the legacy global SALT_KEY when auth hasn't resolved yet
// (e.g. very first PIN setup before Firebase auth state is known).
function getSaltStorageKey() {
  const uid = auth?.currentUser?.uid;
  return uid ? `ds_crypto_salt_${uid}` : SALT_KEY;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function bufferToBase64(buffer) {
  // Avoid spread operator which throws "Maximum call stack size exceeded"
  // for large encrypted buffers (e.g. large journal entries with notes).
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuffer(base64) {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}

// ── Salt: one per account (uid), persisted in localStorage ───────────────────
function getOrCreateSalt() {
  const saltKey  = getSaltStorageKey();
  const existing = localStorage.getItem(saltKey);
  if (existing) return base64ToBuffer(existing);

  const salt = crypto.getRandomValues(new Uint8Array(16));
  localStorage.setItem(saltKey, bufferToBase64(salt));
  return salt;
}

/**
 * FIX #6 (Flag 6): No longer called during ordinary sign-out.
 * Now that the salt is keyed per-uid (see getSaltStorageKey()), different
 * users on a shared device already get isolated salts automatically — the
 * cross-user leakage this originally guarded against can no longer happen,
 * and clearing on sign-out would instead destroy the SAME user's own salt,
 * making their previously PIN-encrypted data undecryptable on next login.
 *
 * Kept as an exported utility for cases that genuinely want to erase a salt
 * (e.g. a future "delete my account" flow) — removes whichever salt
 * getSaltStorageKey() currently resolves to.
 */
export function clearCryptoSalt() {
  localStorage.removeItem(getSaltStorageKey());
}

// ── Derive encryption key from PIN + salt using PBKDF2 ───────────────────────
async function deriveKey(pin, salt) {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: ALGO, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

// ── Encrypt a string (uses device salt) ──────────────────────────────────────
export async function encrypt(plaintext, pin) {
  const salt = getOrCreateSalt();
  const key  = await deriveKey(pin, salt);
  const iv   = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    { name: ALGO, iv },
    key,
    new TextEncoder().encode(plaintext)
  );

  // Layout: [iv (12 bytes)] + [ciphertext]
  const combined = new Uint8Array(iv.byteLength + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.byteLength);

  return bufferToBase64(combined);
}

// ── Decrypt a string with a specific salt ────────────────────────────────────
async function decryptWithSalt(ciphertext, pin, salt) {
  try {
    const key      = await deriveKey(pin, salt);
    const combined = base64ToBuffer(ciphertext);
    const iv       = combined.slice(0, 12);
    const data     = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt({ name: ALGO, iv }, key, data);
    return new TextDecoder().decode(decrypted);
  } catch {
    return null; // Wrong PIN or corrupted data
  }
}

// ── Decrypt using the device's own salt (normal app use) ─────────────────────
export async function decrypt(ciphertext, pin) {
  const salt = getOrCreateSalt();
  return decryptWithSalt(ciphertext, pin, salt);
}

// ── Check if a stored value looks like plain JSON (not encrypted) ─────────────
function looksLikePlainJson(str) {
  if (!str) return false;
  const trimmed = str.trim();
  return (
    trimmed.startsWith('{') ||
    trimmed.startsWith('[') ||
    trimmed.startsWith('"') ||
    trimmed === 'null' ||
    trimmed === 'true' ||
    trimmed === 'false' ||
    /^-?\d/.test(trimmed)
  );
}

// ── Encrypted localStorage wrapper ───────────────────────────────────────────
export const secureStorage = {
  async set(key, value, pin) {
    if (!pin) {
      localStorage.setItem(key, JSON.stringify(value));
      return;
    }
    const encrypted = await encrypt(JSON.stringify(value), pin);
    localStorage.setItem(key, encrypted);
  },

  async get(key, pin) {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    if (!pin) {
      try { return JSON.parse(stored); } catch { return null; }
    }

    try {
      const decrypted = await decrypt(stored, pin);

      if (decrypted !== null) {
        try { return JSON.parse(decrypted); } catch { return null; }
      }

      // Migration case: user just set a PIN but data was stored as plain JSON.
      if (looksLikePlainJson(stored)) {
        try { return JSON.parse(stored); } catch { return null; }
      }

      return null;
    } catch {
      return null;
    }
  },

  remove(key) {
    localStorage.removeItem(key);
  }
};

// ── Encrypted backup export ───────────────────────────────────────────────────
export async function exportEncryptedBackup(data, pin) {
  const salt    = getOrCreateSalt();
  const saltB64 = bufferToBase64(salt);

  const payload   = JSON.stringify({ version: '2.0', exportedAt: new Date().toISOString(), data });
  const encrypted = await encrypt(payload, pin);

  const backupJson = JSON.stringify({ v: 2, salt: saltB64, ct: encrypted });

  const blob = new Blob([backupJson], { type: 'text/plain' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const date = new Date().toISOString().split('T')[0];

  link.href     = url;
  link.download = `sheepheard-backup-${date}.enc`;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Encrypted backup import ───────────────────────────────────────────────────
export async function importEncryptedBackup(fileText, pin) {
  let ciphertext;
  let salt;

  try {
    const parsed = JSON.parse(fileText.trim());

    if (parsed.v === 2 && parsed.salt && parsed.ct) {
      salt       = base64ToBuffer(parsed.salt);
      ciphertext = parsed.ct;
    } else {
      salt       = getOrCreateSalt();
      ciphertext = fileText.trim();
    }
  } catch {
    salt       = getOrCreateSalt();
    ciphertext = fileText.trim();
  }

  const decrypted = await decryptWithSalt(ciphertext, pin, salt);
  if (!decrypted) throw new Error('Wrong PIN or corrupted backup file.');

  const result = JSON.parse(decrypted);
  if (!result.data) throw new Error('Invalid backup file structure.');

  return result;
}